import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import toast from 'react-hot-toast';
import {
  Package, Plus, Edit2, X, AlertTriangle, CheckCircle2, Search, RefreshCw,
  Cloud, Settings, Clock, Trash2, Save, ExternalLink,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const CATEGORIAS = ['Filtros', 'Óleos', 'Peças gerais', 'Elementos', 'Válvulas', 'Outros'];

const emptyPecaForm = () => ({
  codigo: '',
  codigo_fabricante: '',
  nome: '',
  descricao: '',
  categoria: 'Filtros',
  marca: '',
  custo: 0,
  preco_venda: 0,
  unidade_medida: 'un',
  estoque_minimo_carro: 2,
  estoque_maximo_carro: 10,
  ativo: true,
  m8_produto_id: '',
});

const statusItem = (item) => {
  if (item.quantidade === 0) return { label: 'ZERADO', className: 'bg-red-100 text-red-700' };
  if (item.quantidade < item.quantidade_minima) return { label: 'BAIXO', className: 'bg-amber-100 text-amber-700' };
  return { label: 'OK', className: 'bg-emerald-100 text-emerald-700' };
};

const Estoque = () => {
  const { user } = useAuth();
  const podeReposicoes = !user?.funcao?.permissoes?.length
    || user.funcao.permissoes.includes('reposicoes');
  const [tab, setTab] = useState('tecnico');

  const [tecnicos, setTecnicos] = useState([]);
  const [tecnicoId, setTecnicoId] = useState('');
  const [estoque, setEstoque] = useState(null);
  const [alertas, setAlertas] = useState([]);
  const [searchEstoque, setSearchEstoque] = useState('');
  const [loadingEstoque, setLoadingEstoque] = useState(true);

  const [pecas, setPecas] = useState([]);
  const [searchCatalogo, setSearchCatalogo] = useState('');
  const [loadingCatalogo, setLoadingCatalogo] = useState(true);

  const [showPecaModal, setShowPecaModal] = useState(false);
  const [editingPeca, setEditingPeca] = useState(null);
  const [pecaForm, setPecaForm] = useState(emptyPecaForm());
  const [syncingM8, setSyncingM8] = useState(false);

  // Configurações M8
  const [m8Config, setM8Config] = useState({
    m8_sync_ativo: true,
    m8_sync_horarios: ['08:00', '12:00', '18:00'],
    ultima_sync: null,
    ultima_sync_status: null,
  });
  const [loadingM8Config, setLoadingM8Config] = useState(false);
  const [novoHorario, setNovoHorario] = useState('');
  const [syncingTodos, setSyncingTodos] = useState(false);
  const [syncingEquipamentos, setSyncingEquipamentos] = useState(false);
  const [salvandoMinimoId, setSalvandoMinimoId] = useState(null);
  const [reposicoes, setReposicoes] = useState([]);
  const [logsReposicao, setLogsReposicao] = useState([]);
  const [loadingReposicoes, setLoadingReposicoes] = useState(false);
  const [filtroReposicao, setFiltroReposicao] = useState('abertas');

  const loadTecnicos = useCallback(async () => {
    try {
      const res = await api.get('/users', { params: { funcao: 'tecnico' } });
      const list = res.data?.users || [];
      setTecnicos(list);
      if (list.length > 0) {
        setTecnicoId((prev) => prev || list[0].id);
      }
    } catch {
      toast.error('Erro ao carregar técnicos');
    }
  }, []);

  const loadEstoqueTecnico = useCallback(async () => {
    if (!tecnicoId) return;
    setLoadingEstoque(true);
    try {
      const [estRes, alertRes] = await Promise.all([
        api.get(`/estoque/tecnico/${tecnicoId}`),
        api.get(`/estoque/tecnico/${tecnicoId}/alertas`),
      ]);
      setEstoque(estRes.data);
      setAlertas(alertRes.data?.alertas || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar estoque');
      setEstoque(null);
      setAlertas([]);
    } finally {
      setLoadingEstoque(false);
    }
  }, [tecnicoId]);

  const loadPecas = useCallback(async () => {
    setLoadingCatalogo(true);
    try {
      const params = { limit: 500, ativo: true };
      if (searchCatalogo.trim()) params.search = searchCatalogo.trim();
      const res = await api.get('/pecas', { params });
      setPecas(res.data?.pecas || []);
    } catch {
      toast.error('Erro ao carregar catálogo de peças');
    } finally {
      setLoadingCatalogo(false);
    }
  }, [searchCatalogo]);

  const loadM8Config = useCallback(async () => {
    try {
      setLoadingM8Config(true);
      const res = await api.get('/m8/sync/config');
      setM8Config(res.data);
    } catch (err) {
      console.error('Erro ao carregar config M8:', err);
    } finally {
      setLoadingM8Config(false);
    }
  }, []);

  useEffect(() => { loadTecnicos(); }, [loadTecnicos]);
  const loadReposicoes = useCallback(async () => {
    setLoadingReposicoes(true);
    try {
      const params = {};
      if (tecnicoId) params.tecnico_id = tecnicoId;
      if (filtroReposicao === 'abertas') params.abertas = true;
      else if (filtroReposicao !== 'todas') params.status = filtroReposicao;
      const [repRes, logRes] = await Promise.all([
        api.get('/estoque/reposicoes', { params }),
        api.get('/estoque/reposicao/logs', {
          params: tecnicoId ? { tecnico_id: tecnicoId } : {},
        }),
      ]);
      setReposicoes(repRes.data?.reposicoes || []);
      setLogsReposicao(logRes.data?.logs || []);
    } catch {
      toast.error('Erro ao carregar requisições');
      setReposicoes([]);
      setLogsReposicao([]);
    } finally {
      setLoadingReposicoes(false);
    }
  }, [tecnicoId, filtroReposicao]);

  useEffect(() => { if (tab === 'tecnico' && tecnicoId) loadEstoqueTecnico(); }, [tab, tecnicoId, loadEstoqueTecnico]);
  useEffect(() => {
    if (tab === 'config') {
      loadM8Config();
    }
  }, [tab, loadM8Config]);
  useEffect(() => {
    if (tab === 'catalogo') {
      const t = setTimeout(loadPecas, searchCatalogo ? 400 : 0);
      return () => clearTimeout(t);
    }
  }, [tab, searchCatalogo, loadPecas]);
  useEffect(() => {
    if (tab === 'reposicoes') loadReposicoes();
  }, [tab, loadReposicoes]);

  const savePeca = async (e) => {
    e.preventDefault();
    try {
      if (editingPeca) {
        await api.patch(`/pecas/${editingPeca.id}`, pecaForm);
        toast.success('Peça atualizada!');
      } else {
        await api.post('/pecas', pecaForm);
        toast.success('Peça cadastrada!');
      }
      setShowPecaModal(false);
      setEditingPeca(null);
      setPecaForm(emptyPecaForm());
      loadPecas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar peça');
    }
  };

  const sincronizarM8 = async () => {
    if (!tecnicoId) return;
    setSyncingM8(true);
    try {
      const res = await api.post(`/estoque/m8/sync/${tecnicoId}?use_report=true`);
      const data = res.data;
      const metodo =
        data.metodo === 'relatorio_xml' || data.metodo === 'relatorio_csv'
          ? 'via relatório M8'
          : 'via grid';
      if (data.itens_atualizados === 0 && !data.mensagem) {
        toast.error(
          `Sincronização sem itens. M8 retornou ${data.total_produtos_m8 ?? 0} produtos — verifique o destino M8 do técnico.`
        );
      } else {
        toast.success(
          `${data.mensagem || `Sincronizado ${metodo}!`} ${data.itens_atualizados} itens${
            data.pecas_criadas > 0 ? `, ${data.pecas_criadas} peças novas no catálogo` : ''
          }`
        );
      }
      loadEstoqueTecnico();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao sincronizar com M8';
      toast.error(msg);
    } finally {
      setSyncingM8(false);
    }
  };

  const saveM8Config = async () => {
    try {
      await api.put('/m8/sync/config', {
        m8_sync_ativo: m8Config.m8_sync_ativo,
        m8_sync_horarios: m8Config.m8_sync_horarios,
      });
      toast.success('Configurações de sincronização salvas!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar configurações');
    }
  };

  const adicionarHorario = () => {
    if (!novoHorario || !/^\d{2}:\d{2}$/.test(novoHorario)) {
      toast.error('Formato inválido. Use HH:MM (ex: 08:00)');
      return;
    }
    if (m8Config.m8_sync_horarios.includes(novoHorario)) {
      toast.error('Horário já existe');
      return;
    }
    const horarios = [...m8Config.m8_sync_horarios, novoHorario].sort();
    setM8Config({ ...m8Config, m8_sync_horarios: horarios });
    setNovoHorario('');
  };

  const removerHorario = (horario) => {
    const horarios = m8Config.m8_sync_horarios.filter(h => h !== horario);
    setM8Config({ ...m8Config, m8_sync_horarios: horarios });
  };

  const sincronizarTodosTecnicos = async () => {
    setSyncingTodos(true);
    try {
      const res = await api.post('/m8/sync/todos?use_report=true');
      const data = res.data;

      if (data.sucesso) {
        toast.success(
          `Sincronização concluída! ${data.sucessos} técnicos sincronizados${
            data.falhas > 0 ? ` (${data.falhas} falhas)` : ''
          }`
        );
      } else {
        toast.error(`Falha na sincronização: ${data.falhas} técnicos com erro`);
      }

      loadM8Config();
      if (tecnicoId) loadEstoqueTecnico();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao sincronizar todos os técnicos';
      toast.error(msg);
    } finally {
      setSyncingTodos(false);
    }
  };

  const sincronizarEquipamentosM8 = async () => {
    setSyncingEquipamentos(true);
    try {
      const res = await api.post('/m8/equipamentos/sync', null, { timeout: 300000 });
      const data = res.data;
      toast.success(
        `Catálogo de equipamentos sincronizado! ${data.total} equipamentos${
          data.ultima_sync ? ` (atualizado em ${new Date(data.ultima_sync).toLocaleString('pt-BR')})` : ''
        }`
      );
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao sincronizar equipamentos do M8';
      toast.error(msg);
    } finally {
      setSyncingEquipamentos(false);
    }
  };

  const openEditPeca = (peca) => {
    setEditingPeca(peca);
    setPecaForm({
      codigo: peca.codigo || '',
      codigo_fabricante: peca.codigo_fabricante || '',
      nome: peca.nome || '',
      descricao: peca.descricao || '',
      categoria: peca.categoria || 'Filtros',
      m8_produto_id: peca.m8_produto_id || '',
      marca: peca.marca || '',
      custo: peca.custo ?? 0,
      preco_venda: peca.preco_venda ?? 0,
      unidade_medida: peca.unidade_medida || 'un',
      estoque_minimo_carro: peca.estoque_minimo_carro ?? 2,
      estoque_maximo_carro: peca.estoque_maximo_carro ?? 10,
      ativo: peca.ativo !== false,
    });
    setShowPecaModal(true);
  };

  const salvarMinimoItem = async (item, valorRaw) => {
    if (!tecnicoId || !item?.peca_id) return;
    const valor = Math.max(0, parseInt(valorRaw, 10));
    if (Number.isNaN(valor)) {
      toast.error('Mínimo inválido');
      return;
    }
    if (valor === item.quantidade_minima) return;

    setSalvandoMinimoId(item.peca_id);
    try {
      await api.patch(`/estoque/tecnico/${tecnicoId}/item/${item.peca_id}/minimo`, {
        quantidade_minima: valor,
      });
      setEstoque((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          itens: (prev.itens || []).map((i) =>
            i.peca_id === item.peca_id ? { ...i, quantidade_minima: valor } : i
          ),
        };
      });
      toast.success(`Mínimo de ${item.codigo || item.nome} atualizado para ${valor}`);
      // Recarrega alertas (status BAIXO/OK)
      try {
        const alertRes = await api.get(`/estoque/tecnico/${tecnicoId}/alertas`);
        setAlertas(alertRes.data?.alertas || []);
      } catch {
        /* alertas opcionais */
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar mínimo');
    } finally {
      setSalvandoMinimoId(null);
    }
  };

  const itensFiltrados = (estoque?.itens || []).filter((item) => {
    if (!searchEstoque.trim()) return true;
    const q = searchEstoque.toLowerCase();
    return item.nome.toLowerCase().includes(q) || item.codigo.toLowerCase().includes(q);
  });

  const tecnicoSelecionado = tecnicos.find((t) => t.id === tecnicoId);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="estoque-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Package className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Estoque</h1>
            <p className="text-slate-600">Estoque dos técnicos sincronizado com o M8</p>
          </div>
        </div>
        <div className="flex rounded-xl bg-slate-100 p-1">
          <button
            type="button"
            onClick={() => setTab('tecnico')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === 'tecnico' ? 'bg-white shadow text-slate-900' : 'text-slate-600'}`}
          >
            Estoque por técnico
          </button>
          <button
            type="button"
            onClick={() => setTab('catalogo')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === 'catalogo' ? 'bg-white shadow text-slate-900' : 'text-slate-600'}`}
          >
            Catálogo de peças
          </button>
          <button
            type="button"
            onClick={() => setTab('reposicoes')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === 'reposicoes' ? 'bg-white shadow text-slate-900' : 'text-slate-600'}`}
          >
            Requisições
          </button>
          <button
            type="button"
            onClick={() => setTab('config')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${tab === 'config' ? 'bg-white shadow text-slate-900' : 'text-slate-600'}`}
          >
            <Settings className="w-4 h-4" /> Integração M8
          </button>
        </div>
      </div>

      {tab === 'tecnico' && (
        <>
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-600 mb-1">Técnico</label>
              <select
                value={tecnicoId}
                onChange={(e) => setTecnicoId(e.target.value)}
                className="w-full md:max-w-xs border border-slate-200 rounded-xl px-4 py-3 bg-white"
              >
                {tecnicos.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}{t.m8_destino_estoque_id ? ` (M8 ${t.m8_destino_estoque_id})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar peça no estoque..."
                value={searchEstoque}
                onChange={(e) => setSearchEstoque(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl"
              />
            </div>
            <button
              type="button"
              onClick={sincronizarM8}
              disabled={syncingM8 || !tecnicoId}
              className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-3 rounded-xl font-medium flex items-center gap-2 disabled:opacity-50"
            >
              <Cloud className="w-5 h-5" />
              {syncingM8 ? 'Sincronizando...' : 'Sincronizar M8'}
            </button>
            <button
              type="button"
              onClick={loadEstoqueTecnico}
              className="p-3 border border-slate-200 rounded-xl hover:bg-slate-50"
              title="Atualizar"
            >
              <RefreshCw className="w-5 h-5 text-slate-600" />
            </button>
          </div>

          {alertas.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-900">{alertas.length} alerta(s) de estoque — {tecnicoSelecionado?.name}</p>
                <ul className="mt-2 text-sm text-amber-800 space-y-1">
                  {alertas.slice(0, 5).map((a, i) => (
                    <li key={i}>• {a.mensagem}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {loadingEstoque ? (
            <div className="flex justify-center py-12">
              <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
                <span className="text-slate-600 text-sm">
                  {itensFiltrados.length} item(ns) · Valor total: R$ {(estoque?.valor_total ?? 0).toFixed(2)}
                </span>
                {alertas.length === 0 && itensFiltrados.length > 0 && (
                  <span className="flex items-center gap-1 text-emerald-600 text-sm">
                    <CheckCircle2 className="w-4 h-4" /> Estoque OK
                  </span>
                )}
              </div>
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Código</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Peça</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Categoria</th>
                    <th className="text-center py-4 px-6 text-sm font-medium text-slate-600">Qtd</th>
                    <th className="text-center py-4 px-6 text-sm font-medium text-slate-600">Mín</th>
                  </tr>
                </thead>
                <tbody>
                  {itensFiltrados.map((item) => {
                    const st = statusItem(item);
                    return (
                      <tr key={item.peca_id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-4 px-6">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${st.className}`}>{st.label}</span>
                        </td>
                        <td className="py-4 px-6 text-sm text-slate-600">{item.codigo}</td>
                        <td className="py-4 px-6 text-sm font-medium text-slate-900">{item.nome}</td>
                        <td className="py-4 px-6 text-sm text-slate-600">{item.categoria}</td>
                        <td className="py-4 px-6 text-sm text-center font-semibold">{item.quantidade}</td>
                        <td className="py-4 px-6 text-sm text-center">
                          <input
                            type="number"
                            min={0}
                            defaultValue={item.quantidade_minima}
                            key={`${item.peca_id}-${item.quantidade_minima}`}
                            disabled={salvandoMinimoId === item.peca_id}
                            title="Editar mínimo (Enter ou sai do campo para salvar)"
                            className="w-16 mx-auto text-center border border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                            onBlur={(e) => salvarMinimoItem(item, e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.currentTarget.blur();
                              }
                            }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {itensFiltrados.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  Nenhum item no estoque deste técnico.
                  <p className="mt-2 text-sm text-slate-400">
                    Configure o destino M8 em Usuários e sincronize o estoque.
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {tab === 'reposicoes' && (
        <>
          {podeReposicoes && (
            <div className="flex justify-end">
              <Link
                to="/reposicoes"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
              >
                <ExternalLink className="w-4 h-4" />
                Abrir painel de requisições
              </Link>
            </div>
          )}
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-600 mb-1">Técnico</label>
              <select
                value={tecnicoId}
                onChange={(e) => setTecnicoId(e.target.value)}
                className="w-full md:max-w-xs border border-slate-200 rounded-xl px-4 py-3 bg-white"
              >
                {tecnicos.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Filtro</label>
              <select
                value={filtroReposicao}
                onChange={(e) => setFiltroReposicao(e.target.value)}
                className="border border-slate-200 rounded-xl px-4 py-3 bg-white"
              >
                <option value="abertas">Em aberto</option>
                <option value="entregue">Recebidas</option>
                <option value="todas">Todas</option>
              </select>
            </div>
            <button
              type="button"
              onClick={loadReposicoes}
              className="px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-700 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Atualizar
            </button>
          </div>

          {loadingReposicoes ? (
            <div className="flex justify-center py-12">
              <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Nº</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Itens</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Solicitado</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Recebido</th>
                  </tr>
                </thead>
                <tbody>
                  {reposicoes.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100">
                      <td className="py-3 px-4 text-sm font-semibold capitalize">{r.status}</td>
                      <td className="py-3 px-4 text-sm font-mono">{r.numero || r.label || (r.id ? `#${String(r.id).slice(0, 8).toUpperCase()}` : '—')}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {(r.itens || []).map((i) => `${i.codigo || i.nome}×${i.quantidade_solicitada}`).join(', ')}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-500">
                        {r.data_solicitacao ? new Date(r.data_solicitacao).toLocaleString('pt-BR') : '—'}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-500">
                        {r.data_entrega ? new Date(r.data_entrega).toLocaleString('pt-BR') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {reposicoes.length === 0 && (
                <div className="text-center py-10 text-slate-500">Nenhuma requisição encontrada.</div>
              )}
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="font-bold text-slate-900 mb-3">Logs — “não recebi” (Registrar KM)</h3>
            {logsReposicao.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhum log ainda.</p>
            ) : (
              <ul className="space-y-2">
                {logsReposicao.slice(0, 30).map((log) => (
                  <li key={log.id} className="text-sm text-slate-700 border-b border-slate-100 pb-2">
                    <span className="font-medium">{log.tecnico_nome}</span>
                    {' · '}
                    <span className="text-slate-500">{log.data_referencia}</span>
                    {' · '}
                    {log.label || log.solicitacao_id?.slice(0, 8)}
                    <div className="text-xs text-slate-400 mt-0.5">
                      {log.data_hora ? new Date(log.data_hora).toLocaleString('pt-BR') : ''}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      {tab === 'catalogo' && (
        <>
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar por nome ou código..."
                value={searchCatalogo}
                onChange={(e) => setSearchCatalogo(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl"
              />
            </div>
            <button
              type="button"
              onClick={() => { setEditingPeca(null); setPecaForm(emptyPecaForm()); setShowPecaModal(true); }}
              className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2"
            >
              <Plus className="w-5 h-5" /> Nova peça
            </button>
          </div>

          {loadingCatalogo ? (
            <div className="flex justify-center py-12">
              <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Código</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Nome</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Categoria</th>
                    <th className="text-right py-4 px-6 text-sm font-medium text-slate-600">Custo</th>
                    <th className="text-right py-4 px-6 text-sm font-medium text-slate-600">Venda</th>
                    <th className="text-center py-4 px-6 text-sm font-medium text-slate-600">Mínimo</th>
                    <th className="text-right py-4 px-6 text-sm font-medium text-slate-600">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {pecas.map((peca) => (
                    <tr key={peca.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-4 px-6 text-sm font-mono text-slate-700">{peca.codigo}</td>
                      <td className="py-4 px-6 text-sm font-medium text-slate-900">{peca.nome}</td>
                      <td className="py-4 px-6 text-sm text-slate-600">{peca.categoria}</td>
                      <td className="py-4 px-6 text-sm text-right">R$ {(peca.custo ?? 0).toFixed(2)}</td>
                      <td className="py-4 px-6 text-sm text-right">R$ {(peca.preco_venda ?? 0).toFixed(2)}</td>
                      <td className="py-4 px-6 text-sm text-center text-slate-500">
                        {peca.estoque_minimo_carro}
                      </td>
                      <td className="py-4 px-6 text-right">
                        <button type="button" onClick={() => openEditPeca(peca)} className="text-blue-600 p-2 rounded-lg hover:bg-blue-50">
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pecas.length === 0 && (
                <div className="text-center py-12 text-slate-500">Nenhuma peça no catálogo</div>
              )}
            </div>
          )}
        </>
      )}

      {tab === 'config' && (
        <div className="space-y-6">
          {/* Card de sincronização de equipamentos */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                <Cloud className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Catálogo de Equipamentos</h3>
                <p className="text-sm text-slate-600">Sincronize os modelos de equipamentos do M8 (100GPD, 500GPD, etc.)</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">
                  Usado na tela de manutenção para seleção do equipamento.
                </p>
              </div>
              <button
                type="button"
                onClick={sincronizarEquipamentosM8}
                disabled={syncingEquipamentos}
                className="bg-gradient-to-r from-violet-500 to-purple-600 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {syncingEquipamentos ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sincronizando...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-5 h-5" />
                    Sincronizar Equipamentos
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Card de sincronização manual */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Cloud className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Sincronização de Estoque</h3>
                <p className="text-sm text-slate-600">Sincronize todos os técnicos com o M8 agora</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div>
                {m8Config.ultima_sync && (
                  <p className="text-sm text-slate-500">
                    Última sincronização: {new Date(m8Config.ultima_sync).toLocaleString('pt-BR')}
                    {m8Config.ultima_sync_status && (
                      <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                        m8Config.ultima_sync_status === 'sucesso'
                          ? 'bg-emerald-100 text-emerald-700'
                          : m8Config.ultima_sync_status === 'parcial'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {m8Config.ultima_sync_status}
                      </span>
                    )}
                  </p>
                )}
                {!m8Config.ultima_sync && (
                  <p className="text-sm text-slate-400">Nenhuma sincronização realizada ainda</p>
                )}
              </div>
              <button
                type="button"
                onClick={sincronizarTodosTecnicos}
                disabled={syncingTodos}
                className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {syncingTodos ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sincronizando...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-5 h-5" />
                    Sincronizar Agora
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Card de horários automáticos */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">Sincronização Automática</h3>
                  <p className="text-sm text-slate-600">
                    Executada pelo backend nos horários abaixo (fuso America/Sao_Paulo)
                  </p>
                </div>
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={m8Config.m8_sync_ativo}
                  onChange={(e) => setM8Config({ ...m8Config, m8_sync_ativo: e.target.checked })}
                  className="w-5 h-5 text-emerald-600 rounded"
                />
                <span className="text-sm font-medium text-slate-700">Ativo</span>
              </label>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Salve após alterar. O servidor precisa estar em execução para disparar nos horários.
            </p>

            {/* Lista de horários */}
            <div className="space-y-3 mb-4">
              {m8Config.m8_sync_horarios.length === 0 && (
                <p className="text-sm text-slate-400 italic">Nenhum horário configurado</p>
              )}
              {m8Config.m8_sync_horarios.map((horario) => (
                <div key={horario} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span className="font-mono text-slate-700">{horario}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removerHorario(horario)}
                    className="text-red-500 hover:text-red-600 p-1 rounded hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Adicionar horário */}
            <div className="flex gap-3 mb-4">
              <input
                type="time"
                value={novoHorario}
                onChange={(e) => setNovoHorario(e.target.value)}
                className="border border-slate-200 rounded-xl px-4 py-2"
                placeholder="08:00"
              />
              <button
                type="button"
                onClick={adicionarHorario}
                disabled={!novoHorario}
                className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-xl font-medium flex items-center gap-2 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" /> Adicionar
              </button>
            </div>

            {/* Botão salvar */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={saveM8Config}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2"
              >
                <Save className="w-4 h-4" /> Salvar Configurações
              </button>
            </div>
          </div>

          {/* Info sobre destinos */}
          <div className="bg-slate-50 rounded-2xl p-4">
            <h4 className="font-medium text-slate-700 mb-2">Destinos M8 Configurados</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-white rounded-lg p-3 text-center">
                <span className="text-lg font-bold text-blue-600">5</span>
                <p className="text-xs text-slate-500">Isaque</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <span className="text-lg font-bold text-blue-600">10</span>
                <p className="text-xs text-slate-500">Mario</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <span className="text-lg font-bold text-blue-600">12</span>
                <p className="text-xs text-slate-500">Daniel</p>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <span className="text-lg font-bold text-blue-600">13</span>
                <p className="text-xs text-slate-500">Luis</p>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-3">
              Configure o destino M8 para cada carro na página "Carros"
            </p>
          </div>
        </div>
      )}

      {showPecaModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 my-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{editingPeca ? 'Editar peça' : 'Nova peça'}</h2>
              <button type="button" onClick={() => setShowPecaModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={savePeca} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Código *</label>
                  <input required value={pecaForm.codigo} onChange={(e) => setPecaForm({ ...pecaForm, codigo: e.target.value })} className="w-full border rounded-xl px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Categoria</label>
                  <select value={pecaForm.categoria} onChange={(e) => setPecaForm({ ...pecaForm, categoria: e.target.value })} className="w-full border rounded-xl px-3 py-2">
                    {CATEGORIAS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Nome *</label>
                <input required value={pecaForm.nome} onChange={(e) => setPecaForm({ ...pecaForm, nome: e.target.value })} className="w-full border rounded-xl px-3 py-2" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Custo (R$)</label>
                  <input type="number" step="0.01" value={pecaForm.custo} onChange={(e) => setPecaForm({ ...pecaForm, custo: parseFloat(e.target.value) || 0 })} className="w-full border rounded-xl px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Preço venda (R$)</label>
                  <input type="number" step="0.01" value={pecaForm.preco_venda} onChange={(e) => setPecaForm({ ...pecaForm, preco_venda: parseFloat(e.target.value) || 0 })} className="w-full border rounded-xl px-3 py-2" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Mínimo no estoque</label>
                <input type="number" value={pecaForm.estoque_minimo_carro} onChange={(e) => setPecaForm({ ...pecaForm, estoque_minimo_carro: parseInt(e.target.value, 10) || 0 })} className="w-full border rounded-xl px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  ID Produto M8
                  <span className="text-xs text-slate-400 ml-1">(para integração com ERP)</span>
                </label>
                <input
                  type="number"
                  value={pecaForm.m8_produto_id}
                  onChange={(e) => setPecaForm({ ...pecaForm, m8_produto_id: e.target.value })}
                  placeholder="Ex: 8090"
                  className="w-full border rounded-xl px-3 py-2"
                />
              </div>
              <button type="submit" className="w-full bg-emerald-600 text-white py-3 rounded-xl font-medium">
                {editingPeca ? 'Salvar alterações' : 'Cadastrar peça'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Estoque;
